import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { useCurrentUser } from '@/lib/auth-context';
import { canAccessPath } from '@/lib/role-access';

import { ISSUE_FIELD_KEYS, ISSUE_SECTION_KEYS, TARGET_RECORD_TYPES } from './catalog';
import { resolveTargetNavigation } from './navigation';
import type { IssuePresentation, IssueTarget } from './types';

/**
 * The "record label: business code" line of a target, shared by the full card
 * and compact previews so both name a record the same way. Returns null when
 * the target names no record (no type, or a monthly batch without a code);
 * `code` is null when the record exists but its code is unavailable.
 * Kept in this file (like useIssueText beside AuditIssueMessage) so the UI
 * evidence audit, which pins this file, covers the card's record line.
 */
export const useTargetRecord = (
  target: IssueTarget | null | undefined,
): { recordType: string; label: string; code: string | null } | null => {
  const { t } = useTranslation(['issues']);
  const recordType = target?.record_type ?? null;
  if (!target || !recordType) return null;
  if (!target.record_code && recordType === 'monthly_batch') return null;
  const label = (TARGET_RECORD_TYPES as readonly string[]).includes(recordType)
    ? t(`efs.issues.record_types.${recordType}`)
    : t('efs.issues.target.record_generic');
  return { recordType, label, code: target.record_code ?? null };
};

/**
 * Human-readable target context + the allowlisted navigation action
 * (spec-13 §6.3). Display order: monthly summary sheet, record label +
 * business code, section/field. UUIDs never appear here.
 */
export const AuditTargetContext = ({
  target,
  className,
}: {
  target: IssueTarget | null | undefined;
  className?: string;
}) => {
  const { t } = useTranslation(['issues']);
  const record = useTargetRecord(target);
  if (!target) return null;

  const section =
    target.section_key && (ISSUE_SECTION_KEYS as readonly string[]).includes(target.section_key)
      ? t(`efs.issues.sections.${target.section_key}`)
      : '';
  const field =
    target.field_key && (ISSUE_FIELD_KEYS as readonly string[]).includes(target.field_key)
      ? t(`efs.issues.fields.${target.field_key}`)
      : '';

  return (
    <div className={className}>
      {target.batch_display ? (
        <p className="text-xs text-efs-text-muted">
          <span className="mr-1">{t('efs.issues.target.batch_label')}:</span>
          <span className="font-medium text-efs-text-primary">{target.batch_display}</span>
        </p>
      ) : null}
      {record ? (
        <p className="text-xs text-efs-text-muted">
          <span className="mr-1">{record.label}:</span>
          {record.code ? (
            <span className="break-all font-medium text-efs-text-primary">{record.code}</span>
          ) : (
            <span>{t('efs.issues.target.code_unavailable')}</span>
          )}
        </p>
      ) : null}
      {section ? (
        <p className="text-xs text-efs-text-muted">
          <span className="mr-1">{t('efs.issues.target.section_label')}:</span>
          <span>{section}</span>
          {field ? <span> / {field}</span> : null}
        </p>
      ) : null}
    </div>
  );
};

/**
 * The primary "open target" action. Resolves the typed navigation intent via
 * the allowlist; renders a localized disabled explanation when the target is
 * unavailable or the caller is not authorized (spec-13 §6.2/§7.1).
 */
export const AuditTargetLink = ({
  presentation,
  actionLabel,
  onNavigate,
  size = 'sm',
}: {
  presentation: IssuePresentation;
  /** Localized action text (falls back to the catalog next-action). */
  actionLabel?: string | null;
  /** Called just before navigation (e.g. close a dialog). */
  onNavigate?: (path: string) => void;
  size?: 'sm' | 'default';
}) => {
  const { t } = useTranslation(['issues']);
  const navigate = useNavigate();
  const user = useCurrentUser();

  const target = presentation.target ?? null;
  const resolved = resolveTargetNavigation(target);
  const label = actionLabel || t('efs.issues.target.open');

  // Server verdict: the record is missing, foreign, or the caller may not
  // open it (spec-13 §4.2/§11) — localized unavailable identity. Round-3
  // item 16: the nearest AUTHORIZED parent-batch fallback still renders, so
  // the operator is never silently stranded.
  if (presentation.can_open_target === false) {
    const batchPath = target?.monthly_batch_id ? `/batches/${target.monthly_batch_id}` : null;
    const batchAllowed = batchPath ? canAccessPath(user, batchPath) : false;
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-efs-text-muted">{t('efs.issues.target.unavailable')}</span>
        {batchAllowed && batchPath ? (
          <Button
            type="button"
            variant="outline"
            size={size}
            className="h-auto min-h-11 max-w-full whitespace-normal text-left"
            onClick={() => {
              onNavigate?.(batchPath);
              navigate(batchPath);
            }}
          >
            {t('efs.issues.target.open_batch')}
            <ArrowUpRight className="ml-1 h-3.5 w-3.5 shrink-0" aria-hidden />
          </Button>
        ) : null}
      </div>
    );
  }

  // Defense in depth: re-check the centralized frontend route-access
  // contract before rendering an enabled action (round-2 item 1).
  if (resolved.ok && !canAccessPath(user, resolved.path)) {
    return <p className="text-xs text-efs-text-muted">{t('efs.issues.target.no_permission')}</p>;
  }

  if (!resolved.ok && resolved.fallbackPath && !canAccessPath(user, resolved.fallbackPath)) {
    return <p className="text-xs text-efs-text-muted">{t('efs.issues.target.no_permission')}</p>;
  }

  if (!resolved.ok) {
    return (
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-xs text-efs-text-muted">{t(resolved.reasonKey)}</span>
        {resolved.fallbackPath ? (
          <Button
            type="button"
            variant="outline"
            size={size}
            className="h-auto min-h-11 max-w-full whitespace-normal text-left"
            onClick={() => {
              onNavigate?.(resolved.fallbackPath as string);
              navigate(resolved.fallbackPath as string);
            }}
          >
            {t('efs.issues.target.open_batch')}
            <ArrowUpRight className="ml-1 h-3.5 w-3.5 shrink-0" aria-hidden />
          </Button>
        ) : null}
      </div>
    );
  }

  return (
    <Button
      type="button"
      variant="outline"
      size={size}
      // Long localized action labels wrap instead of forcing horizontal
      // overflow at 375px (spec-13 §12).
      className="h-auto min-h-11 max-w-full whitespace-normal text-left"
      onClick={() => {
        onNavigate?.(resolved.path);
        navigate(resolved.path);
      }}
    >
      {label}
      <ArrowUpRight className="ml-1 h-3.5 w-3.5 shrink-0" aria-hidden />
    </Button>
  );
};
